import type { AxiosResponse } from "axios";

import { httpClient } from "../../../shared/lib/api";
import type {
	DoorRuleOptions,
	DoorRulePayload,
	DoorRuleProps,
	ListDoorRulesResponse,
} from "../types/door-rule";

const BASE_URL = "precificador/door-count-rules/";

const GENERIC_MESSAGE =
	"Não foi possível concluir a operação. Tente novamente.";

export type DoorRuleErrorKind =
	| "detail"
	| "validation"
	| "forbidden"
	| "unavailable"
	| "unknown";

export class DoorRuleRequestError extends Error {
	kind: DoorRuleErrorKind;
	status: number;
	field?: string;

	constructor(
		kind: DoorRuleErrorKind,
		status: number,
		message: string,
		field?: string,
	) {
		super(message);
		this.name = "DoorRuleRequestError";
		this.kind = kind;
		this.status = status;
		this.field = field;
	}
}

const requestConfig = { validateStatus: () => true };

function toRequestError(response: AxiosResponse): DoorRuleRequestError {
	const { status, data } = response;

	if (status === 400 || status === 404 || status === 409) {
		const detail =
			typeof data?.detail === "string" ? data.detail : GENERIC_MESSAGE;
		return new DoorRuleRequestError("detail", status, detail);
	}
	if (status === 422) {
		const field = Array.isArray(data?.detail)
			? data.detail[0]?.loc?.[1]
			: undefined;
		return new DoorRuleRequestError(
			"validation",
			status,
			GENERIC_MESSAGE,
			field,
		);
	}
	if (status === 403) {
		return new DoorRuleRequestError(
			"forbidden",
			status,
			"Seu perfil não tem acesso às regras de portas.",
		);
	}
	if (status === 502 || status === 503 || status === 504) {
		return new DoorRuleRequestError(
			"unavailable",
			status,
			"Precificador indisponível",
		);
	}
	return new DoorRuleRequestError("unknown", status, GENERIC_MESSAGE);
}

function unwrap<T>(response: AxiosResponse<T>): T {
	if (response.status >= 200 && response.status < 300) return response.data;
	throw toRequestError(response);
}

export async function listDoorRules(
	params: URLSearchParams,
): Promise<ListDoorRulesResponse> {
	const query = params.toString();
	const url = query ? `${BASE_URL}?${query}` : BASE_URL;
	return unwrap(
		await httpClient.get<ListDoorRulesResponse>(url, requestConfig),
	);
}

export async function getDoorRuleOptions(): Promise<DoorRuleOptions> {
	return unwrap(
		await httpClient.get<DoorRuleOptions>(`${BASE_URL}options/`, requestConfig),
	);
}

export async function getDoorRule(id: string): Promise<DoorRuleProps> {
	return unwrap(
		await httpClient.get<DoorRuleProps>(`${BASE_URL}${id}/`, requestConfig),
	);
}

export async function createDoorRule(
	payload: DoorRulePayload,
): Promise<DoorRuleProps> {
	return unwrap(
		await httpClient.post<DoorRuleProps>(BASE_URL, payload, requestConfig),
	);
}

export async function updateDoorRule(
	id: string,
	payload: DoorRulePayload,
): Promise<DoorRuleProps> {
	return unwrap(
		await httpClient.patch<DoorRuleProps>(
			`${BASE_URL}${id}/`,
			payload,
			requestConfig,
		),
	);
}
